import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SqlImportsDatabaseAttributes } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { invalidateSqlImportDatabaseLists, removeSqlImportDatabaseCache, setSqlImportDatabaseCache } from './cache';
import { parseSqlImportDatabase, type SqlImportDatabase } from './entity';

/**
 * The connection settings a caller writes — the generated `sql_import_database`
 * body, so the write surface cannot drift from the spec. Only `type` is
 * required by the schema; which other fields the backend requires depends on
 * the engine (host/port/name/username for TCP engines, `project_id` for
 * BigQuery, `pem_private_key` for Snowflake), and the form's own schema is
 * what enforces that.
 */
export type SqlImportDatabaseWriteInput = SqlImportsDatabaseAttributes;

export interface CreateSqlImportDatabaseInput extends SqlImportDatabaseWriteInput {
  workspaceId: number;
}

/**
 * Save a new database. The backend opens a connection with the submitted
 * settings before saving, so a 422 with a `database` error is a connection
 * failure, not a form-field one. Allow for the ~25s verification timeout.
 *
 * @public
 */
export async function createSqlImportDatabase({
  workspaceId,
  ...write
}: CreateSqlImportDatabaseInput): Promise<SqlImportDatabase> {
  const data = await hydraUiApi.sqlImportDatabases.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { sql_import_database: write },
  });
  return parseSqlImportDatabase(data.sql_import_database);
}

type UseCreateSqlImportDatabaseVariables = Omit<CreateSqlImportDatabaseInput, 'workspaceId'>;

interface UseCreateSqlImportDatabaseOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<SqlImportDatabase, UseCreateSqlImportDatabaseVariables>;
}

/** @public */
export const useCreateSqlImportDatabase = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseCreateSqlImportDatabaseOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseCreateSqlImportDatabaseVariables) =>
      createSqlImportDatabase({ workspaceId, ...variables }),
    onSuccess: (created, ...callbackArgs) => {
      setSqlImportDatabaseCache(qc, workspaceId, created);
      invalidateSqlImportDatabaseLists(qc, workspaceId);
      onSuccess?.(created, ...callbackArgs);
    },
  });
};

export interface UpdateSqlImportDatabaseInput extends SqlImportDatabaseWriteInput {
  workspaceId: number;
  databaseId: string;
}

/**
 * Replace a database's connection settings; verified the same way as a create.
 *
 * @public
 */
export async function updateSqlImportDatabase({
  workspaceId,
  databaseId,
  ...write
}: UpdateSqlImportDatabaseInput): Promise<SqlImportDatabase> {
  const data = await hydraUiApi.sqlImportDatabases.update({
    params: { path: { environment_id: String(workspaceId), id: databaseId } },
    body: { sql_import_database: write },
  });
  return parseSqlImportDatabase(data.sql_import_database);
}

type UseUpdateSqlImportDatabaseVariables = Omit<UpdateSqlImportDatabaseInput, 'workspaceId'>;

interface UseUpdateSqlImportDatabaseOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<SqlImportDatabase, UseUpdateSqlImportDatabaseVariables>;
}

/** @public */
export const useUpdateSqlImportDatabase = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseUpdateSqlImportDatabaseOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUpdateSqlImportDatabaseVariables) =>
      updateSqlImportDatabase({ workspaceId, ...variables }),
    onSuccess: (updated, ...callbackArgs) => {
      setSqlImportDatabaseCache(qc, workspaceId, updated);
      invalidateSqlImportDatabaseLists(qc, workspaceId);
      onSuccess?.(updated, ...callbackArgs);
    },
  });
};

export interface DeleteSqlImportDatabaseInput {
  workspaceId: number;
  databaseId: string;
}

/** @public */
export async function deleteSqlImportDatabase({
  workspaceId,
  databaseId,
}: DeleteSqlImportDatabaseInput): Promise<void> {
  await hydraUiApi.sqlImportDatabases.delete({
    params: { path: { environment_id: String(workspaceId), id: databaseId } },
  });
}

type UseDeleteSqlImportDatabaseVariables = Omit<DeleteSqlImportDatabaseInput, 'workspaceId'>;

interface UseDeleteSqlImportDatabaseOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, UseDeleteSqlImportDatabaseVariables>;
}

/** @public */
export const useDeleteSqlImportDatabase = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: UseDeleteSqlImportDatabaseOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseDeleteSqlImportDatabaseVariables) =>
      deleteSqlImportDatabase({ workspaceId, ...variables }),
    onSuccess: (result, variables, ...callbackArgs) => {
      removeSqlImportDatabaseCache(qc, workspaceId, variables.databaseId);
      invalidateSqlImportDatabaseLists(qc, workspaceId);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};
